/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-general
 * @responsibility Compose independently tuned desktop and mobile SET-F04 General pane surfaces.
 * @state-owner React transient edit/pending form state; parent owns server state
 * @allowed-imports React; shared translator; @vispo/contracts types
 * @forbidden direct API client; member-management UI; a delete-organization button or dialog
 * @public-api GeneralSettingsSurface
 * @side-effects invokes parent commands only
 * @tests GeneralSettingsSurface.test.tsx
 */

import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslator } from '../../../shared/model/use-workspace';
import './settings-general.css';

// Mirrors the backend's own allowlist and cap (identity_access/service.py's
// _IMAGE_UPLOAD_MIME_TYPES / _IMAGE_UPLOAD_MAX_BYTES) so an obviously-invalid selection is
// rejected immediately, before any network round trip -- the server stays the authoritative
// validator (magic bytes, dimensions) for everything this cheap client-side check can't see.
const LOGO_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

function firstGrapheme(value: string): string {
  return Array.from(value.trim())[0]?.toLocaleUpperCase() || '組';
}

function isValidLogoSelection(file: File): boolean {
  return LOGO_MIME_TYPES.has(file.type) && file.size > 0 && file.size <= LOGO_MAX_BYTES;
}

export interface GeneralSettingsSurfaceProps {
  canManage: boolean;
  errorCode: string | null;
  logoUploadErrorCode: string | null;
  logoUrl: string | null;
  mobile: boolean;
  name: string;
  onClearLogo(): void;
  onResetLogoUploadError(): void;
  onRetry(): void;
  onSave(name: string): void;
  onUploadLogo(file: File): void;
  saving: boolean;
  uploadingLogo: boolean;
}

export function GeneralSettingsSurface(props: GeneralSettingsSurfaceProps) {
  const t = useTranslator();
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(props.name);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [pendingLogoPreviewUrl, setPendingLogoPreviewUrl] = useState<string | null>(null);
  const [logoSelectionInvalid, setLogoSelectionInvalid] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const previousLogoUrl = useRef(props.logoUrl);

  const startEdit = () => {
    setDraftName(props.name);
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);
  const save = () => {
    props.onSave(draftName);
    setEditing(false);
  };
  const trimmedDraft = draftName.trim();
  const isDirty = trimmedDraft !== props.name;
  const isValid = trimmedDraft.length >= 1 && trimmedDraft.length <= 160;
  const canSave = isDirty && isValid;

  const clearLogoSelection = () => {
    if (pendingLogoPreviewUrl) URL.revokeObjectURL(pendingLogoPreviewUrl);
    setPendingLogoFile(null);
    setPendingLogoPreviewUrl(null);
    setLogoSelectionInvalid(false);
  };

  // props.logoUrl changing (for ANY reason -- a successful upload, or the separate "移除 Logo"
  // clear action, both of which flow through their own independent mutation) means server state
  // has moved and the local pre-upload preview is now obsolete; release it. A failed upload
  // never changes logoUrl, so this deliberately leaves the preview and the selected file in
  // place on error, letting the same picked file be retried without reselecting.
  // biome-ignore lint/correctness/useExhaustiveDependencies: clearLogoSelection intentionally excluded -- it closes over pendingLogoPreviewUrl, which changes on every file selection and would re-run this change-detection effect on selection alone, not just when logoUrl itself changes
  useEffect(() => {
    if (previousLogoUrl.current !== props.logoUrl) {
      clearLogoSelection();
    }
    previousLogoUrl.current = props.logoUrl;
  }, [props.logoUrl]);

  const handleLogoFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    props.onResetLogoUploadError();
    if (!isValidLogoSelection(file)) {
      clearLogoSelection();
      setLogoSelectionInvalid(true);
      return;
    }
    if (pendingLogoPreviewUrl) URL.revokeObjectURL(pendingLogoPreviewUrl);
    setLogoSelectionInvalid(false);
    setPendingLogoFile(file);
    setPendingLogoPreviewUrl(URL.createObjectURL(file));
  };

  const cancelLogoSelection = () => {
    clearLogoSelection();
    props.onResetLogoUploadError();
  };

  const confirmLogoUpload = () => {
    if (pendingLogoFile) props.onUploadLogo(pendingLogoFile);
  };

  return (
    <section
      aria-label={t('General')}
      className={`general-settings-pane${props.mobile ? ' general-settings-pane--mobile' : ' general-settings-pane--desktop'}`}
    >
      {props.errorCode === 'ORGANIZATION_REVISION_CONFLICT' ? (
        <div className="general-settings-pane__error" role="alert">
          <span>{t('已被他人修改')}</span>
          <button type="button" onClick={props.onRetry}>
            {t('重新整理')}
          </button>
        </div>
      ) : props.errorCode ? (
        <div className="general-settings-pane__error" role="alert">
          <span>{t('更新失敗，請重試')}</span>
          <button type="button" onClick={props.onRetry}>
            {t('重新整理')}
          </button>
        </div>
      ) : null}

      <div className="general-settings-pane__identity">
        <span className="general-settings-pane__mark" aria-hidden="true">
          {pendingLogoPreviewUrl ? (
            <img alt="" src={pendingLogoPreviewUrl} />
          ) : props.logoUrl ? (
            <img alt="" src={props.logoUrl} />
          ) : (
            firstGrapheme(props.name)
          )}
        </span>
        <div className="general-settings-pane__name">
          {!props.canManage ? (
            <b>{props.name}</b>
          ) : editing ? (
            <>
              <input
                aria-label={t('組織名稱')}
                disabled={props.saving}
                maxLength={160}
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
              />
              <div className="general-settings-pane__actions">
                <button disabled={props.saving} type="button" onClick={cancelEdit}>
                  {t('取消')}
                </button>
                <button
                  className="general-settings-pane__primary"
                  disabled={!canSave || props.saving}
                  type="button"
                  onClick={save}
                >
                  {props.saving ? t('儲存中…') : t('儲存')}
                </button>
              </div>
            </>
          ) : (
            <>
              <b>{props.name}</b>
              <button type="button" onClick={startEdit}>
                {t('編輯')}
              </button>
            </>
          )}
        </div>
      </div>

      {props.canManage ? (
        <div className="general-settings-pane__logo-upload">
          <input
            accept="image/png,image/jpeg,image/webp"
            aria-label={t('上傳 Logo')}
            className="general-settings-pane__logo-input"
            disabled={props.uploadingLogo}
            ref={logoInputRef}
            type="file"
            onChange={handleLogoFileChange}
          />
          {pendingLogoFile ? (
            <div className="general-settings-pane__actions">
              <button disabled={props.uploadingLogo} type="button" onClick={cancelLogoSelection}>
                {t('取消')}
              </button>
              <button
                className="general-settings-pane__primary"
                disabled={props.uploadingLogo}
                type="button"
                onClick={confirmLogoUpload}
              >
                {props.uploadingLogo ? t('上傳中…') : t('上傳')}
              </button>
            </div>
          ) : null}
          {logoSelectionInvalid || props.logoUploadErrorCode ? (
            <p className="general-settings-pane__error" role="alert">
              {t('僅支援 PNG/JPEG/WEBP，上限 2 MB')}
            </p>
          ) : null}
          {props.logoUrl && !pendingLogoFile ? (
            <button
              className="general-settings-pane__clear-logo"
              disabled={props.uploadingLogo}
              type="button"
              onClick={props.onClearLogo}
            >
              {t('移除 Logo')}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="general-settings-pane__section">
        <span className="general-settings-pane__label">{t('刪除組織')}</span>
        <p>
          {t(
            '目前尚未提供自助刪除組織的流程。如需刪除組織，請透過下方聯絡我們與我們聯繫，我們會協助您完成後續程序。',
          )}
        </p>
        <a href="mailto:support@vispo.app">{t('聯絡我們')}</a>
      </div>
    </section>
  );
}
